import Footer from "@/components/layout/Footer";
import Header from "@/components/layout/Header";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { motion } from "framer-motion";
import { HelpCircle, Plus, ShieldCheck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  FaCheck,
  FaCreditCard,
  FaDollarSign,
  FaGem,
  FaLock,
  FaRupeeSign,
  FaShieldAlt,
  FaStar,
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import NormalLoader from "../components/customeLoader/NormalLoader";
import { fetchIpData } from "../Redux/ReduxSlices/newsearchSlice";
import { fetchSubscriptions } from "../Redux/ReduxSlices/priceSlice";
import { fetchUserDetails } from "../Redux/ReduxSlices/profileSlice";
import Checkout from "./payment/CheckoutPage";
import PaymentPopup from "./payment/PaymentPopup";
import Meta from "../components/Meta/Meta";

const stripePromise = loadStripe(import.meta.env.VITE_BACKEND_STRIPE_LIVE_PK);

const GridPattern = ({ className = "" }) => (
  <svg
    className={className}
    viewBox="0 0 800 400"
    fill="none"
    aria-hidden="true"
  >
    <defs>
      <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
        <path d="M 40 0 L 0 0 0 40" stroke="rgba(99,102,241,0.12)" />
      </pattern>
      <linearGradient id="fade" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="rgba(255,255,255,0)" />
        <stop offset="0.15" stopColor="rgba(255,255,255,0.25)" />
        <stop offset="1" stopColor="rgba(255,255,255,0.9)" />
      </linearGradient>
    </defs>
    <rect width="800" height="400" fill="url(#grid)" />
    <rect width="800" height="400" fill="url(#fade)" />
  </svg>
);

const DiagramOrbits = ({ className = "" }) => (
  <svg
    className={className}
    viewBox="0 0 900 500"
    fill="none"
    aria-hidden="true"
  >
    <defs>
      <linearGradient id="g1" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="rgba(99,102,241,0.35)" />
        <stop offset="1" stopColor="rgba(168,85,247,0.25)" />
      </linearGradient>
      <linearGradient id="g2" x1="1" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="rgba(168,85,247,0.35)" />
        <stop offset="1" stopColor="rgba(236,72,153,0.18)" />
      </linearGradient>
    </defs>

    {/* Orbits */}
    <circle
      cx="530"
      cy="250"
      r="160"
      stroke="rgba(99,102,241,0.18)"
      strokeWidth="2"
      strokeDasharray="6 10"
    />
    <circle
      cx="530"
      cy="250"
      r="220"
      stroke="rgba(168,85,247,0.16)"
      strokeWidth="2"
      strokeDasharray="8 14"
    />
    <circle
      cx="530"
      cy="250"
      r="285"
      stroke="rgba(236,72,153,0.10)"
      strokeWidth="2"
      strokeDasharray="10 18"
    />

    {/* Nodes */}
    <circle cx="430" cy="150" r="10" fill="url(#g1)" />
    <circle cx="700" cy="220" r="12" fill="url(#g2)" />
    <circle cx="610" cy="430" r="8" fill="url(#g1)" />
    <circle cx="320" cy="320" r="7" fill="url(#g2)" />

    {/* Chart line */}
    <path
      d="M120 360 C 200 320, 260 340, 330 300 C 400 260, 470 290, 520 250 C 590 195, 690 245, 780 190"
      stroke="rgba(99,102,241,0.18)"
      strokeWidth="3"
      fill="none"
    />
    <path
      d="M120 360 C 200 320, 260 340, 330 300 C 400 260, 470 290, 520 250 C 590 195, 690 245, 780 190"
      stroke="rgba(168,85,247,0.14)"
      strokeWidth="8"
      fill="none"
      opacity="0.35"
    />
  </svg>
);

const FloatingBlob = ({
  className = "",
  gradient = "from-indigo-200/40 to-purple-200/30",
}) => (
  <motion.div
    className={`absolute rounded-full blur-3xl ${className} bg-gradient-to-br ${gradient}`}
    initial={{ opacity: 0.35, scale: 1 }}
    animate={{ opacity: [0.25, 0.45, 0.25], scale: [1, 1.06, 1] }}
    transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
    aria-hidden="true"
  />
);

const DRAFT_KEY = "campaign_draft_v1";

function PricingPlan({ onSuccess }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showRazorpay, setShowRazorpay] = useState(false);
  const dispatch = useDispatch();
  const { subscriptions, loading } = useSelector((state) => state.pricing);
  const { ipData } = useSelector((state) => state.database);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const draftFromState = location.state?.draft || null;
  const returnTo = location.state?.from || "/create-campaign";

  const draftFromStorage = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "null");
    } catch {
      return null;
    }
  }, []);

  const countryName =
    localStorage.getItem("user_country_name") ||
    ipData?.data?.country_name ||
    "India";
  const isIndia = countryName.toLowerCase() === "india";
  const currency = isIndia ? "INR" : "USD";

  // const isIndia = "india";
  // const currency = "INR";

  // useEffect(() => {
  //   // 1️⃣ Check if country exists in localStorage
  //   const savedCountry = localStorage.getItem("user_country_name");

  //   if (savedCountry) {
  //     return; // ⛔ Do not call API again
  //   }

  //   // 2️⃣ No saved country? Fetch from API and store it.
  //   dispatch(fetchIpData()).then((res) => {
  //     const country = res?.payload?.data?.country_name;
  //     if (country) {
  //       localStorage.setItem("user_country_name", country);
  //     }
  //   });
  // }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSubscriptions());
    dispatch(fetchUserDetails());
  }, [dispatch]);

  const selectedPlan = useMemo(() => {
    if (!subscriptions?.data?.items) return null;
    const plan = subscriptions.data.items.find(
      (p) => p.currency.toUpperCase() === currency,
    );
    return plan || null;
  }, [subscriptions, currency]);

  const handlePaymentSuccess = () => {
    const baseDraft = draftFromState || draftFromStorage || {};
    const updated = { ...baseDraft, paymentSuccess: true };
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(updated));
    onSuccess?.();
    navigate(returnTo, {
      replace: true,
      state: {
        draft: updated,
        paymentSuccess: true,
        intent: baseDraft?.intent || "idle",
      },
    });
  };

  if (loading || !selectedPlan) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600 animate-pulse">
        <NormalLoader />
      </div>
    );
  }

  const selectedPlan1 = {
    subscription_pk: "92ab5583-3dad-44dd-b683-5c94a61dc81c",
    num_of_assessments: 1,
    title: "Small Team",
    sub_text: "₹199 per assessment",
    price: 0,
    smallest_unit_price: 699900,
    per_assessment_cost: 6999,
    description: [
      "Free Search",
      "Send first free 50 emails",
      "Fully customized AI email templates",
      "Live email support",
      "No payment required",
    ],
    note: null,
    button_txt: "Buy 10 Assessments",
    highlighted: false,
    yearly_only: false,
    currency: "INR",
    created_at: "2025-04-23T06:26:28.901000",
    updated_at: "2025-04-23T06:26:28.901000",
    is_active: true,
    plan_type: "company",
  };

  return (
    <>
      <>
        <div className="relative min-h-screen bg-gradient-to-b from-white via-white to-indigo-50/30 ">
          <Header />
          <Meta
          title="Pricing Plans | MaxiPitch AI Sales Automation Platform"
          description="Check MaxiPitch pricing plans to find the right AI sales automation solution. Flexible subscriptions designed for lead generation, outbound marketing, and business growth."
          url="https://maxipitch.com/pricing"
        />

          {/* ===== GLOBAL PAGE BACKDROP (vector/diagram theme) ===== */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <FloatingBlob className="-top-24 -left-24 w-80 h-80" />
            <FloatingBlob
              className="top-10 -right-24 w-96 h-96"
              gradient="from-purple-200/40 to-pink-200/25"
            />
            <FloatingBlob
              className="bottom-0 left-1/3 w-[28rem] h-[28rem]"
              gradient="from-indigo-200/25 to-cyan-200/15"
            />

            {/* subtle grid */}
            <motion.div
              className="absolute inset-0 opacity-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              transition={{ duration: 0.8 }}
            >
              <GridPattern className="absolute top-0 left-1/2 -translate-x-1/2 w-[1300px] max-w-none" />
            </motion.div>

            {/* orbit diagram (top-right) */}
            <motion.div
              className="absolute -right-40 sm:-right-10 top-6 w-[860px] max-w-none opacity-70"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 0.7, y: 0 }}
              transition={{ duration: 0.9, ease: "easeOut" }}
            >
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{
                  duration: 9,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <DiagramOrbits />
              </motion.div>
            </motion.div>
          </div>

          {/* ===== HERO ===== */}
          <section className="relative pt-10 sm:pt-14 pb-10 sm:pb-14 px-4">
            <div className="relative max-w-6xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65, ease: "easeOut" }}
                className="text-center"
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-indigo-100 to-purple-100 border border-indigo-200/50">
                  <FaStar className="text-indigo-500" />
                  <span className="text-sm font-semibold text-indigo-700">
                    Transparent Pricing
                  </span>
                </div>

                <h1 className="mt-5  tracking-tight bg-gradient-to-r from-gray-900 via-indigo-800 to-purple-800 bg-clip-text text-transparent">
                  Choose Your Plan
                </h1>

                <p className="mt-4 text-base sm:text-xl text-gray-600 max-w-2xl mx-auto">
                  Simple, transparent pricing with everything you need to
                  succeed
                </p>
              </motion.div>
            </div>
          </section>

          {/* ===== VALUE PROP ===== */}
          <section className="relative py-13 px-4 mb-12">
            <div className="relative max-w-6xl mx-auto">
              <div className="rounded-3xl border border-indigo-100 bg-white/75 backdrop-blur-xl shadow-sm p-6 sm:p-10">
                <div className="max-w-4xl mx-auto text-center">
                  <p className="font-semibold mb-3 text-xl sm:text-2xl text-gray-800 leading-relaxed">
                    Hiring MaxiPitch is like onboarding the smartest salesperson
                    on the planet — available 24 hours a day — for just{" "}
                    <span className="text-blue-600 font-extrabold">
                      <span className=" ml-1 mr-1">
                        {" "}
                        {selectedPlan.currency === "USD" ? "$" : "₹"}{" "}
                      </span>
                      {selectedPlan.price}
                      <span className="text-lg">/Month</span>
                    </span>
                  </p>
                  <p className="text-gray-500 text-base sm:text-lg">
                    The ultimate sales powerhouse for your products and services
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ===== PRICING (your preferred classic cards) ===== */}
          <section className="relative max-w-6xl mx-auto px-4 pb-20">
            <div className="grid md:grid-cols-3 gap-10 items-stretch">
              {/* FREE */}
              <motion.div
                whileHover={{ y: -8 }}
                transition={{ duration: 0.4 }}
                className="relative bg-white/85 backdrop-blur-xl rounded-3xl border border-gray-200 shadow-xl p-8 overflow-hidden flex flex-col h-full"
              >
                {/* subtle inner diagram for the card */}
                <div className="pointer-events-none absolute inset-0 opacity-40">
                  <motion.div
                    className="absolute -top-10 -right-10 w-72 h-72 rounded-full bg-gradient-to-br from-indigo-200/30 to-purple-200/20 blur-2xl"
                    animate={{ scale: [1, 1.06, 1] }}
                    transition={{
                      duration: 10,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                  <svg
                    className="absolute -bottom-10 -left-10 w-[380px] opacity-60"
                    viewBox="0 0 400 200"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M20 160 C 100 100, 140 170, 220 120 C 270 90, 330 120, 380 70"
                      stroke="rgba(99,102,241,0.20)"
                      strokeWidth="3"
                    />
                    {[100, 220, 330].map((x, i) => (
                      <circle
                        key={i}
                        cx={x}
                        cy={i === 0 ? 112 : i === 1 ? 120 : 122}
                        r="7"
                        fill="rgba(99,102,241,0.18)"
                      />
                    ))}
                  </svg>
                </div>

                <div className="relative flex flex-col h-full">
                  <span className="relative -top-4 left-0 bg-indigo-600 text-white px-4 py-1 text-xs rounded-full shadow inline-block w-fit">
                    FREE
                  </span>

                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-800 mt-2 mb-4">
                      Explore the platform
                    </h3>

                    <div className="flex items-center mb-6">
                      {isIndia ? (
                        <FaRupeeSign className="text-gray-600 text-2xl" />
                      ) : (
                        <FaDollarSign className="text-gray-600 text-2xl" />
                      )}
                      <span className="text-4xl font-extrabold text-gray-900 ml-1">
                        {selectedPlan1.price}
                      </span>
                    </div>

                    <ul className="space-y-3 mb-8">
                      {selectedPlan1.description.map((f, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <FaCheck className="text-green-600 mt-1 flex-shrink-0" />
                          <span className="text-gray-700">{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-auto">
                    <button
                      onClick={() => navigate("/")}
                      className="w-full py-3 border border-indigo-600 rounded-md font-semibold hover:bg-indigo-50 transition"
                    >
                      Start Free
                    </button>
                    <p className="text-center text-sm text-gray-500 mt-3">
                      No credit card required
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* PRO */}
              <motion.div
                whileHover={{ y: -8 }}
                transition={{ duration: 0.4 }}
                className="relative bg-gradient-to-br from-white/90 to-indigo-50/80 backdrop-blur-xl rounded-3xl border border-indigo-300 shadow-2xl p-8 overflow-hidden flex flex-col h-full"
              >
                {/* subtle inner diagram for the card */}
                <div className="pointer-events-none absolute inset-0 opacity-40">
                  <motion.div
                    className="absolute -top-12 -left-12 w-80 h-80 rounded-full bg-gradient-to-br from-purple-200/30 to-pink-200/20 blur-2xl"
                    animate={{ scale: [1, 1.07, 1] }}
                    transition={{
                      duration: 11,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                  <svg
                    className="absolute -bottom-12 -right-10 w-[420px] opacity-65"
                    viewBox="0 0 420 220"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle
                      cx="270"
                      cy="120"
                      r="90"
                      stroke="rgba(168,85,247,0.16)"
                      strokeWidth="2"
                      strokeDasharray="6 10"
                    />
                    <circle
                      cx="270"
                      cy="120"
                      r="130"
                      stroke="rgba(99,102,241,0.12)"
                      strokeWidth="2"
                      strokeDasharray="8 14"
                    />
                    <path
                      d="M40 170 C 130 120, 170 190, 250 135 C 300 102, 350 130, 400 80"
                      stroke="rgba(99,102,241,0.18)"
                      strokeWidth="3"
                    />
                  </svg>
                </div>

                <div className="relative flex flex-col h-full">
                  <span className="relative -top-4 left-0 bg-purple-600 text-white px-4 py-1 text-xs rounded-full shadow inline-block w-fit">
                    PRO
                  </span>

                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-800 mt-2 mb-4">
                      Built to scale
                    </h3>

                    <div className="flex items-center mb-6">
                      {isIndia ? (
                        <FaRupeeSign className="text-gray-600 text-2xl" />
                      ) : (
                        <FaDollarSign className="text-gray-600 text-2xl" />
                      )}
                      <span className="text-4xl font-extrabold text-gray-900 ml-1">
                        {selectedPlan.price}
                        <span className="text-lg">/Month</span>
                      </span>
                    </div>

                    <ul className="space-y-3 mb-8">
                      {selectedPlan.description.map((f, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <FaCheck className="text-indigo-600 mt-1 flex-shrink-0" />
                          <span className="text-gray-700">{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-auto">
                    <button
                      onClick={() =>
                        isIndia ? setShowRazorpay(true) : setIsModalOpen(true)
                      }
                      className="w-full py-3 rounded-md main-button text-white font-bold shadow-lg hover:-translate-y-1 transition"
                    >
                      Get Started
                    </button>
                    <p className="text-center text-sm text-gray-500 mt-3">
                      No hidden charges
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* ENTERPRISE PLAN */}
              <motion.div
                whileHover={{ y: -8 }}
                transition={{ duration: 0.4 }}
                className="relative bg-gradient-to-br from-white/90 to-indigo-50/80 backdrop-blur-xl rounded-3xl border border-indigo-300 shadow-2xl p-8 overflow-hidden flex flex-col h-full"
              >
                {/* subtle inner diagram for the card */}
                <div className="pointer-events-none absolute inset-0 opacity-40">
                  <motion.div
                    className="absolute -top-12 -left-12 w-80 h-80 rounded-full bg-gradient-to-br from-purple-200/30 to-pink-200/20 blur-2xl"
                    animate={{ scale: [1, 1.07, 1] }}
                    transition={{
                      duration: 11,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                  <svg
                    className="absolute -bottom-12 -right-10 w-[420px] opacity-65"
                    viewBox="0 0 420 220"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle
                      cx="270"
                      cy="120"
                      r="90"
                      stroke="rgba(168,85,247,0.16)"
                      strokeWidth="2"
                      strokeDasharray="6 10"
                    />
                    <circle
                      cx="270"
                      cy="120"
                      r="130"
                      stroke="rgba(99,102,241,0.12)"
                      strokeWidth="2"
                      strokeDasharray="8 14"
                    />
                    <path
                      d="M40 170 C 130 120, 170 190, 250 135 C 300 102, 350 130, 400 80"
                      stroke="rgba(99,102,241,0.18)"
                      strokeWidth="3"
                    />
                  </svg>
                </div>

                <div className="relative flex flex-col h-full">
                  <span className="relative -top-4 left-0 bg-purple-600 text-white px-4 py-1 text-xs rounded-full shadow inline-block w-fit">
                    ENTERPRISE PLAN
                  </span>

                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-800 mt-2 mb-4">
                      Most Powerful
                    </h3>

                    <div className="flex items-center mb-6 text-3xl font-extrabold text-gray-900">
                      Custom Pricing
                    </div>

                    <ul className="space-y-3 mb-8">
                      {[
                        "Email ID and / or Mobile Numbers",
                        "In Excel Format",
                        "Data volume as per your need",
                        "Secure delivery & verified contacts",
                        "Data consulting & targeting help",
                        "Delivered within 24 to 48 hrs",
                        "₹ 5 per email contact",
                        "₹ 10 per mobile number",
                      ].map((f, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <FaCheck className="text-indigo-600 mt-1 flex-shrink-0" />
                          <span className="text-gray-700 text-sm">{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-auto">
                    <button
                      onClick={() => navigate("/contact-us")}
                      className="w-full py-3 rounded-md main-button text-white font-bold shadow-lg hover:-translate-y-1 transition"
                    >
                      Contact Sales
                    </button>
                    <p className="text-center text-sm text-gray-500 mt-3">
                      Get high-quality leads tailored to your business.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ===== SECURITY ===== */}
          <section className="relative px-4 pb-6">
            <div className="relative max-w-6xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.65, ease: "easeOut" }}
                className="text-center mb-10"
              >
                <h2 className=" bg-gradient-to-r from-gray-900 to-indigo-800 bg-clip-text text-transparent">
                  Secure & Reliable
                </h2>
                <p className="mt-2 text-base sm:text-xl text-gray-500 max-w-2xl mx-auto">
                  Your security and privacy are our top priorities
                </p>

                <motion.div
                  className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 border border-gray-200 shadow-sm"
                  animate={{ y: [0, -3, 0] }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <ShieldCheck className="h-4 w-4 text-indigo-600" />
                  <span className="text-sm font-semibold text-gray-700">
                    Encrypted payments & protected data
                  </span>
                </motion.div>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-10">
                {[
                  {
                    icon: (
                      <FaLock className="text-2xl sm:text-3xl text-white" />
                    ),
                    title: "Bank-Level Security",
                    description:
                      "All transactions are encrypted with industry-standard security protocols to ensure complete safety.",
                    gradient: "from-blue-500 to-cyan-500",
                  },
                  {
                    icon: (
                      <FaShieldAlt className="text-2xl sm:text-3xl text-white" />
                    ),
                    title: "Data Protection",
                    description:
                      "Your personal and payment data is never shared with third parties. We follow strict privacy compliance.",
                    gradient: "from-emerald-500 to-green-500",
                  },
                  {
                    icon: (
                      <FaCreditCard className="text-2xl sm:text-3xl text-white" />
                    ),
                    title: "Secure Payment",
                    description:
                      "All payments are processed through trusted gateways with full buyer protection.",
                    gradient: "from-violet-500 to-purple-500",
                  },
                ].map((feature, index) => (
                  <motion.div
                    key={index}
                    className="group relative bg-white/80 backdrop-blur-xl rounded-3xl border border-gray-200 shadow-sm p-7 sm:p-8 hover:shadow-xl transition-all duration-300"
                    whileHover={{ y: -4 }}
                  >
                    <div
                      className={`absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-300`}
                    >
                      {feature.icon}
                    </div>
                    <div className="pt-10">
                      <h3 className="">{feature.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* ===== FAQ ===== */}
          <section className="relative py-6 sm:py-8 bg-white">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              <div className="flex justify-center items-center">
                <motion.div
                  className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm max-md:text-xs font-medium text-black border border-indigo-500 mb-6 bg-white"
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                >
                  <HelpCircle className="h-4 w-4 mr-2" />
                  Frequently Asked Questions
                </motion.div>
              </div>

              <div className="text-center mb-8 sm:mb-10">
                <h2 className="">Quick Answers to Common Questions</h2>
                <p className="text-gray-600">
                  Everything you need to know before getting started
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
                {[
                  {
                    question: "Do I need a card to try?",
                    answer:
                      "No. You can start completely free. No credit card is required for the free plan.",
                  },
                  {
                    question: "Does it send from my email ID?",
                    answer:
                      "Yes. All emails are sent directly from your own email address, so your recipients see you as the sender.",
                  },
                  {
                    question: "Will it spam people?",
                    answer:
                      "No. We only send personalized emails to relevant prospects, which helps avoid spam and improves deliverability.",
                  },
                ].map((faq, index) => (
                  <motion.details
                    key={index}
                    className="group border border-blue-400/70 bg-white/70 backdrop-blur-xl rounded-md px-5 sm:px-6 py-4 transition-all hover:shadow-sm"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35 }}
                  >
                    <summary className="flex items-center justify-between cursor-pointer list-none">
                      <span className="font-semibold text-gray-900">
                        {faq.question}
                      </span>

                      <span className="flex items-center justify-center w-9 h-9 rounded-xl main-button text-white text-xl group-open:rotate-45 transition-transform">
                        <Plus className="h-5 w-5 text-white font-extrabold" />
                      </span>
                    </summary>

                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="mt-4 text-gray-600 text-sm leading-relaxed"
                    >
                      {faq.answer}
                    </motion.p>
                  </motion.details>
                ))}
              </div>

              {/* CTA strip */}
              <button
                //  onClick={openLoginModal}
                className="hover:cursor-pointer text-white mt-8"
              >
                Exp
              </button>
            </div>
          </section>

          {isIndia && selectedPlan && (
            <Dialog
              open={showRazorpay}
              onOpenChange={(v) => {
                setShowRazorpay(v);

                // Optional: clear selectedPlan when closing
                // if (!v) setSelectedPlan(null);
              }}
              className="overflow-visible mx-4"
            >
              <DialogContent className="w-[95%] md:max-w-[900px] mx-auto rounded-2xl p-0 overflow-visible">
                <div className="max-h-[90vh] overflow-y-auto p-4">
                  <PaymentPopup
                    showModal={showRazorpay}
                    setShowModal={setShowRazorpay}
                    selectedPlan={selectedPlan}
                    currency="INR"
                    onSuccess={handlePaymentSuccess}
                  />
                </div>
              </DialogContent>
            </Dialog>
          )}

          {/* Stripe Modal */}
          {!isIndia && selectedPlan && (
            <Dialog
              open={isModalOpen}
              onOpenChange={setIsModalOpen}
              className="p-4"
            >
              <DialogContent className="w-[95%] sm:max-w-[600px] mx-auto p-6 rounded-2xl ">
                <h4 className="text-xl md:text-2xl font-bold mb-4">
                  Complete Your Payment
                </h4>
                <Elements stripe={stripePromise}>
                  <Checkout
                    subscriptionPk={selectedPlan.subscription_pk}
                    currency={selectedPlan.currency}
                    planPrice={selectedPlan.price}
                    isModalOpen={isModalOpen}
                    setIsModalOpen={setIsModalOpen}
                    onSuccess={handlePaymentSuccess}
                  />
                </Elements>
              </DialogContent>
            </Dialog>
          )}
          <Footer />
        </div>

        {/* Auth Modal */}

        {/* Waiting List Modal */}
      </>
    </>
  );
}

export default PricingPlan;
